import Select, { ActionMeta } from 'react-select';
import styled, { css } from 'styled-components';
import { COLORS, TYPOGRAPHY } from '@app/constants';
import { forwardRef, ForwardRefRenderFunction, useId, useState } from 'react';
import { ModifiedGroup, ModifiedSelectProps, Option } from './types';
import { components, customStyles } from './common';
import { TagTheme } from './components/Tag/Tag';

interface Props extends ModifiedSelectProps {
  className?: string;
  label?: string;
  tagTheme?: TagTheme;
  defaultValue?: Option;
}

const Dropdown: ForwardRefRenderFunction<any, Props> = (
  {
    options,
    label,
    className,
    onChange,
    error,
    isGroupSelect = false,
    tagTheme = 'primary',
    value,
    ...props
  },
  ref,
) => {
  const [defaultValue, setDefaultValue] = useState<Option | undefined>(
    props.defaultValue,
  );
  const [currentGroup, setCurrentGroup] = useState<ModifiedGroup>(
    options[0] as ModifiedGroup,
  );

  const ownId = useId();
  const id = props.id || ownId;

  const onChangeSelect = (
    newValue: unknown,
    actionMeta: ActionMeta<unknown>,
  ) => {
    setDefaultValue(undefined);
    onChange?.(newValue, actionMeta);
  };

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id} $isDisabled={props.isDisabled}>
          {label}
        </Label>
      )}
      <Root
        onFocus={() =>
          isGroupSelect && setCurrentGroup(options[0] as ModifiedGroup)
        }
        openMenuOnFocus
        aria-errormessage={error}
        inputId={id}
        ref={ref}
        hideSelectedOptions={false}
        isClearable={false}
        backspaceRemovesValue={false}
        closeMenuOnSelect={!props.isMulti}
        controlShouldRenderValue={!props.isMulti}
        onChange={onChangeSelect}
        noOptionsMessage={({ inputValue }: any) => {
          return inputValue ? 'Такого тут нет :(' : 'Введите для поиска';
        }}
        styles={customStyles}
        classNamePrefix="Dropdown"
        placeholder="Выберите из списка"
        components={components}
        options={isGroupSelect && currentGroup ? currentGroup.options : options}
        className={className}
        $isDisabled={props.isDisabled}
        value={defaultValue || value}
        {...props}
        groups={options as ModifiedGroup[]}
        isGroupSelect={isGroupSelect}
        currentGroup={currentGroup}
        setCurrentGroup={setCurrentGroup}
        blurInputOnSelect={false}
      />
      {error?.length && <Error role="alert">{error}</Error>}
    </div>
  );
};

export default forwardRef(Dropdown);

interface StyleProps {
  $isDisabled?: boolean;
}

const Root = styled((props: any) => <Select {...props} />)<StyleProps>`
  width: 100%;
  background-color: ${COLORS.white};
  border-radius: 10px;

  .Dropdown__input {
    box-shadow: none;
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      opacity: 0.5;
    `};
`;

const Label = styled.label<StyleProps>`
  ${TYPOGRAPHY.footnoteMedium13};
  color: ${COLORS.grayscale600};
  margin-bottom: 4px;
  display: block;
  opacity: 0.75;
`;

const Error = styled.p`
  ${TYPOGRAPHY.footnoteMedium13}
  color: ${COLORS.systemError};
  margin-top: 4px;
`;
